/**
 * Hybrid Textarea Component
 * Automatically chooses between Tailwind and styled-components based on props
 */

import * as React from "react";
import Container, { Accessory } from "./styles"; // Styled-components version
import { TextareaTailwind } from "./TextareaTailwind"; // Tailwind version
import { needsStyledComponents } from "@sproutsocial/seeds-react-system-props";
import type { TypeTextareaProps } from "./TextareaTypes";

const TextareaHybrid = React.forwardRef<HTMLTextAreaElement, TypeTextareaProps>(
  (props, ref) => {
    const { innerRef, ...rest } = props;

    // Route to the styled-components path when system props (e.g. mt, px, color)
    // are present or the component was extended via styled(). Everything else
    // gets the lighter Tailwind path.
    if (needsStyledComponents(rest)) {
      const {
        autoFocus,
        disabled,
        readOnly,
        isInvalid,
        id,
        name,
        placeholder,
        value,
        enableSpellcheck,
        enableResize,
        required,
        rows = 4,
        elemBefore,
        elemAfter,
        maxLength,
        ariaLabel,
        ariaDescribedby,
        onBlur,
        onChange,
        onFocus,
        onKeyDown,
        onKeyUp,
        color,
        qa = {},
        inputProps = {},
        ...containerRest
      } = rest;

      return (
        <Container
          hasBeforeElement={!!elemBefore}
          hasAfterElement={!!elemAfter}
          disabled={disabled}
          invalid={isInvalid}
          resizable={enableResize}
          // TODO: fix this type since `color` should be valid here. TS can't resolve the correct type.
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          color={color}
          data-qa-textarea={id}
          data-qa-textarea-isdisabled={disabled === true}
          data-qa-textarea-isrequired={required === true}
          data-qa-textarea-isinvalid={isInvalid === true}
          {...qa}
          {...containerRest}
        >
          {elemBefore && <Accessory before>{elemBefore}</Accessory>}

          <textarea
            id={id}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedby}
            aria-invalid={isInvalid}
            value={value}
            name={name}
            placeholder={placeholder}
            rows={rows}
            disabled={disabled}
            readOnly={Boolean(readOnly)}
            autoFocus={autoFocus}
            spellCheck={enableSpellcheck}
            required={required}
            maxLength={maxLength}
            onChange={onChange}
            onKeyUp={onKeyUp}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            onFocus={onFocus}
            ref={ref || innerRef}
            data-qa-textarea-input=""
            data-qa-input={name}
            {...inputProps}
          />

          {elemAfter && <Accessory after>{elemAfter}</Accessory>}
        </Container>
      );
    }

    // Use Tailwind version (preferred - better performance)
    return <TextareaTailwind {...props} ref={ref || innerRef} />;
  }
);

TextareaHybrid.displayName = "Textarea";
export default TextareaHybrid;
